/**
 * AssignVerifierButton — puts a verifier on an unassigned verification.
 *
 * Both flows already had an assign endpoint that admits an admin:
 *   work       POST /api/verification-requests/:id/assign
 *   contractor POST /api/contractor-verifications/admin/:id/assign-verifier
 *
 * The candidate pool is approved verifiers only, narrowed to those whose
 * verifier level covers this kind of work — a verifier scoped to 'contractor'
 * can't take a work verification, so offering them would invite a failure.
 */

import React, { useState, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { PersonAddAlt1 } from '@mui/icons-material';

import adminService from '../../services/adminService';
import adminApplicationsService from '../../services/adminApplicationsService';
import { brand, status as statusTokens, text } from '../../theme/colors';

// The verifier level each kind of work requires ('client' displays as "Work").
const REQUIRED_LEVEL = { work: 'client', contractor: 'contractor' };

const AssignVerifierButton = ({
  kind,
  row,
  onAssigned,
  size = 'small',
  // Reassignment: a text 'Change' trigger and a note about who holds it now.
  label = 'Assign',
  variant = 'outlined',
  currentVerifierId = null,
  currentVerifierName = null,
}) => {
  const [open, setOpen] = useState(false);
  const [verifiers, setVerifiers] = useState([]);
  const [loadingPool, setLoadingPool] = useState(false);
  const [verifierId, setVerifierId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const requiredLevel = REQUIRED_LEVEL[kind] || 'client';

  const loadVerifiers = useCallback(async () => {
    setLoadingPool(true);
    setError(null);
    try {
      const res = await adminApplicationsService.list({
        role: 'verifier',
        status: 'approved',
        limit: 100,
      });
      const eligible = (res?.applications || [])
        .filter((v) => !v.verifierTypes?.length || v.verifierTypes.includes(requiredLevel))
        // On a reassign, the current holder is not a re-assignment target.
        .filter((v) => !currentVerifierId || v.id !== currentVerifierId);
      setVerifiers(eligible);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not load verifiers.');
    } finally {
      setLoadingPool(false);
    }
  }, [requiredLevel, currentVerifierId]);

  const handleOpen = (e) => {
    e.stopPropagation();
    setVerifierId('');
    setError(null);
    setOpen(true);
    loadVerifiers();
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (kind === 'contractor') {
        // The endpoint keys off the contractor_verifications id.
        if (!row.verificationId) {
          throw new Error('This assignment has no contractor verification file to attach to.');
        }
        await adminService.assignContractorVerifier(row.verificationId, verifierId);
      } else {
        await adminService.assignWorkVerifier(row.id, verifierId);
      }
      setOpen(false);
      onAssigned?.();
    } catch (err) {
      setError(
        err?.response?.data?.message
          || err?.response?.data?.error
          || err?.message
          || 'The verifier could not be assigned.'
      );
    } finally {
      setBusy(false);
    }
  };

  const labelFor = (v) => {
    const name = `${v.firstName || ''} ${v.lastName || ''}`.trim() || v.email;
    const levels = v.verifierTypes?.length
      ? v.verifierTypes.map((t) => (t === 'client' ? 'Work' : 'Contractor')).join(' + ')
      : 'level not set';
    return `${name} · ${levels}`;
  };

  return (
    <>
      <Button
        size={size}
        variant={variant}
        startIcon={variant === 'text' ? null : <PersonAddAlt1 sx={{ fontSize: 16 }} />}
        onClick={handleOpen}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          color: brand.primary,
          ...(variant === 'outlined'
            ? { borderColor: `${brand.primary}66`, '&:hover': { borderColor: brand.primary } }
            : { minWidth: 0, p: variant === 'text' ? 0.25 : undefined }),
        }}
      >
        {label}
      </Button>

      <Dialog open={open} onClose={() => !busy && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {currentVerifierId ? 'Reassign verifier' : 'Assign a verifier'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {kind === 'contractor'
              ? `Vetting for ${row?.contractor?.name || row?.contractor?.email || 'this contractor'}.`
              : `Verification ${row?.id}${row?.projectTitle ? ` · ${row.projectTitle}` : ''}.`}
            {currentVerifierId
              ? ` Currently with ${currentVerifierName || 'a verifier'} — reassigning cancels their assignment and starts a fresh one.`
              : ' They are notified straight away.'}
          </DialogContentText>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loadingPool ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" sx={{ color: text.secondary }}>
                Loading approved verifiers…
              </Typography>
            </Box>
          ) : verifiers.length === 0 ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              No approved verifier is scoped to{' '}
              {requiredLevel === 'client' ? 'Work' : 'Contractor'} verifications.
            </Alert>
          ) : (
            <TextField
              select
              fullWidth
              label="Verifier"
              value={verifierId}
              onChange={(e) => setVerifierId(e.target.value)}
              helperText={`Approved verifiers scoped to ${
                requiredLevel === 'client' ? 'Work' : 'Contractor'
              } verifications.`}
            >
              {verifiers.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {labelFor(v)}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={busy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy || !verifierId}
            onClick={submit}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PersonAddAlt1 />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: statusTokens.success,
              '&:hover': { bgcolor: statusTokens.successDark },
            }}
          >
            Assign verifier
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssignVerifierButton;
